/** Cookie name written from the landing URL → Lead event field. */
export const UTM_COOKIE_TO_LEAD_FIELD = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  utmSiteVisitDate: 'utmSiteVisitDate',
  utmUserAgent: 'utmUserAgent',
} as const

export const UTM_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

const LEAD_FIELD_LIMITS: Partial<Record<string, number>> = {
  utmTerm: 200,
  utmContent: 300,
  utmDestinationUrl: 300,
  utmUserAgent: 300,
}

export const UTM_COOKIE_MAX_AGE_SECONDS = 864000

/**
 * Nuxt's default cookie decode uses destr(), which turns an all-digit value
 * into a number. Campaign and content ids from Google Ads are all digits, so
 * they must stay exact strings or the lead omits them.
 */
export const encodeUtmCookie = (value: unknown) =>
  encodeURIComponent(value == null ? '' : String(value))

export const decodeUtmCookie = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

type QueryValue = string | string[] | null | undefined
type Query = Record<string, QueryValue>

const firstQueryValue = (value: QueryValue) => {
  if (Array.isArray(value)) return value[0] ?? ''
  return value ?? ''
}

export const truncateLeadField = (field: string, value: string) => {
  const limit = LEAD_FIELD_LIMITS[field]
  if (!limit || value.length <= limit) return value
  return value.slice(0, limit)
}

/** `2022-09-01 14:00:00` in UTC so server and browser writes match. */
export const formatUtmSiteVisitDate = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
}

export const hasUtmQuery = (query: Query = {}) =>
  UTM_QUERY_KEYS.some((key) => firstQueryValue(query[key]).trim() !== '')

export type UtmCookieWrites = Partial<Record<keyof typeof UTM_COOKIE_TO_LEAD_FIELD, string>>

/**
 * Values to store when the current URL contains UTM params.
 * Campaign params update on each new campaign landing.
 * Visit date stays from the first landing that had UTMs.
 */
export const buildUtmCookieWrites = ({
  query = {},
  userAgent = '',
  now = new Date(),
  existing = {},
}: {
  query?: Query
  userAgent?: string
  now?: Date
  existing?: { utmSiteVisitDate?: string }
}): UtmCookieWrites => {
  if (!hasUtmQuery(query)) return {}

  const writes: UtmCookieWrites = {}
  for (const key of UTM_QUERY_KEYS) {
    const value = firstQueryValue(query[key]).trim()
    const field = UTM_COOKIE_TO_LEAD_FIELD[key]
    writes[key] = value ? truncateLeadField(field, value) : ''
  }

  if (!existing.utmSiteVisitDate) {
    writes.utmSiteVisitDate = formatUtmSiteVisitDate(now)
  }
  if (userAgent) {
    writes.utmUserAgent = truncateLeadField('utmUserAgent', userAgent)
  }

  return writes
}

/** Destination URL is the page the lead is submitted on, capped at 300 characters. */
export const destinationUrlForLead = (fields: Record<string, string>, currentUrl = '') => {
  const url = currentUrl.trim()
  if (!url || Object.keys(fields).length === 0) return fields
  return {
    ...fields,
    utmDestinationUrl: truncateLeadField('utmDestinationUrl', url),
  }
}

const leadCookieText = (raw: unknown) => {
  if (typeof raw === 'string') return raw.trim()
  if (typeof raw === 'number' || typeof raw === 'boolean' || typeof raw === 'bigint') return String(raw)
  return ''
}

/** Lead event fields from stored cookies. Empty cookies are omitted. */
export const mapUtmCookiesToLeadFields = (
  cookies: Partial<Record<keyof typeof UTM_COOKIE_TO_LEAD_FIELD, unknown>>,
) => {
  const fields: Record<string, string> = {}
  for (const [cookieName, leadField] of Object.entries(UTM_COOKIE_TO_LEAD_FIELD)) {
    const value = leadCookieText(cookies[cookieName as keyof typeof UTM_COOKIE_TO_LEAD_FIELD])
    if (!value) continue
    fields[leadField] = truncateLeadField(leadField, value)
  }
  return fields
}
